import React, {
  useState,
  useCallback,
  useContext,
} from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { PillButton } from '../../../../components/Button/ActionButtons';
import useSessionStore from '../../../../hooks/useSessionStore';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import useToast from '../../../hooks/useToast';
import UserContext from '../../../../context/UserContext';
import LoggedInUserContext from '../../../../context/LoggedInUserContext';

import CreateCoachTaskModal from '../CreateCoachTaskModal';
import { FeatureFlag } from '../../../../utils/postHog';
import {
  StyledContainer,
  StyledTaskIcon,
} from './styles';
import text from './text.json';

const CreateCoachTask = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { isAdmin, isCoachAssistant } = useSessionStore();
  const { userId: coachId } = useContext(UserContext);
  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const isCoachTasksEnabled = useFeatureFlagEnabled(FeatureFlag.ENABLE_COACH_TASKS);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const onModalDismiss = useCallback(() => {
    if (isComponentMountedRef.current) {
      setIsModalOpen(false);
    }
  }, [
    isComponentMountedRef,
  ]);

  const showCreatedToast = useCallback(() => {
    showToast(text.taskCreated);
  }, [
    showToast,
  ]);

  const showErrorToast = useCallback((errorMessage = '') => {
    showToast(errorMessage || text.taskError, { error: true });
  }, [
    showToast,
  ]);

  // Coach tasks are created only by internal staff (admins + ACs/CAMs) and never
  // for one's own dashboard. Real coaches are neither admin nor coach-assistant,
  // so they never see this button (self-creation is out of scope for V1).
  const canCreateCoachTask = (isAdmin || isCoachAssistant) && !!coachId && loggedInUserId !== coachId
    && isCoachTasksEnabled;
  if (!canCreateCoachTask) {
    return null;
  }

  return (
    <StyledContainer>
      <PillButton
        onClick={() => setIsModalOpen(true)}
        icon={<StyledTaskIcon />}
        variant="neutral"
      >
        {text.createTask}
      </PillButton>
      <CreateCoachTaskModal
        isOpen={isModalOpen}
        onDidDismiss={onModalDismiss}
        onCreated={showCreatedToast}
        onError={showErrorToast}
      />
    </StyledContainer>
  );
};

export default CreateCoachTask;
